// frontend/src/services/ai.js

/**
 * AI service to handle chat interactions and history
 */
const aiService = {
  /**
   * Sends a message to the AI Mechanic Agent and streams the response via SSE.
   * NEW: Now accepts an optional imageUrl.
   */
  sendChatMessageStream: async (sessionId, message, imageUrl, onChunkReceived, onStreamEnd, onError) => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';
    const url = `${API_BASE_URL}/ai/chat`;
    const token = localStorage.getItem('accessToken');

    if (!token) {
        const errorMsg = "Authentication token not found.";
        console.error("sendChatMessageStream:", errorMsg);
        onError(errorMsg);
        return { success: false, error: errorMsg };
    }

    try {
      console.log("aiService: Attempting to connect to SSE stream...");
      
      // --- UPDATED PAYLOAD ---
      const payload = {
        session_id: sessionId,
        message: message,
        image_url: imageUrl, // Can be null
      };
      // -----------------------

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorData = { message: `HTTP error! status: ${response.status}` };
        try {
            errorData = await response.json();
        } catch (e) { /* Ignore if response is not JSON */ }
        const errorMsg = errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser or no response body.');
      }

      console.log("aiService: SSE stream connection established.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log("aiService: SSE stream finished.");
          onStreamEnd();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let boundaryIndex;
        while ((boundaryIndex = buffer.indexOf('\n\n')) >= 0) {
            const message = buffer.slice(0, boundaryIndex);
            buffer = buffer.slice(boundaryIndex + 2);

            if (message.startsWith('event: end')) {
                console.log("aiService: Received end event.");
                continue;
            } else if (message.startsWith('event: error')) {
                 console.error("aiService: Received error event.");
                 try {
                     const dataLine = message.split('\n').find(line => line.startsWith('data: '));
                     if (dataLine) {
                         const errorJson = dataLine.substring(6).trim();
                         const errorPayload = JSON.parse(errorJson);
                         onError(errorPayload.error || "Unknown error from server.");
                     } else {
                         onError("Received error event with no data.");
                     }
                 } catch (e) {
                     console.error("Error parsing SSE error event:", e);
                     onError("Failed to parse error event from server.");
                 }
            } else if (message.startsWith('data: ')) {
                const data = message.substring(6).trim();
                try {
                    const parsed = JSON.parse(data);
                    if (parsed.response) {
                        onChunkReceived(parsed.response);
                    } else if (parsed.error) {
                        console.error("aiService: Received error in data payload:", parsed.error);
                        onError(parsed.error);
                    }
                } catch (e) {
                    console.error('Error parsing SSE data JSON:', e, 'Data:', data);
                }
            }
        }
      }

      return { success: true };

    } catch (error) {
      console.error('Error sending/streaming chat message:', error);
      onError(error.message || "Failed to connect or process stream.");
      return { success: false, error: error.message };
    }
  },

  getChatHistory: async (sessionId) => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';
    const url = `${API_BASE_URL}/ai/history/${sessionId}`;
    const token = localStorage.getItem('accessToken');

    if (!token) {
        return { success: false, error: "Authentication token not found." };
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        let errorData = { message: `HTTP error! status: ${response.status}` };
        try {
            errorData = await response.json();
        } catch(e) { /* ignore */ }
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data?.messages) {
        console.log(`aiService: Fetched ${data.messages.length} raw history messages from backend.`);
        const loadedMessages = data.messages.map((msg, index) => {
            console.log(`[getChatHistory] Mapping raw message index ${index}:`, JSON.stringify(msg));
            const messageText = msg.text || '[No Text Found in Backend Data]';
            const mappedMsg = {
                sender: msg.sender,
                text: messageText
            };
            console.log(`[getChatHistory] Mapped message index ${index}:`, JSON.stringify(mappedMsg));
            return mappedMsg;
        });
        return { success: true, history: loadedMessages };
      } else {
        console.warn("aiService: Received unexpected history response structure.", data);
        return { success: true, history: [] };
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      return { success: false, error: error.message || "Failed to load chat history." };
    }
  },
};

export default aiService;